// The normalized in-memory store — SPEC-001 §3: "the whole working set lives
// in the browser". This is the ONLY place a page may read domain data from.
// Pages must never call fetch directly; that is what would break INV-1 (zero
// network calls on navigation).
//
// Shape: normalized by entity type, keyed by id — { employee: {12: {...}},
// location: {MELM: {...}}, ... } — mirrored to IndexedDB so a reload does not
// require a re-hydrate over the network. Phase 1 ships the store and its
// mirror; the WebSocket delta stream that keeps it current lands in a later
// phase (SPEC-001 §3 sequence).
import { useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { idbGetAll, idbPut, idbClearAll, idbDeleteKeys } from './idb';

const RESOURCE_IDB_PREFIX = 'resource:';
let hydrateInflight: Promise<void> | null = null;
/** Bumped by every Sign out (clearStore). A disk read that started before
 * the wipe and finishes after it is dropped, never written back over the
 * empty store (Cursor review #36: the next person on a shared PC saw the
 * previous person's copy). */
let storeEpoch = 0;

export type EntityType = string;
export type EntityId = string | number;

export interface NormalizedState {
  [entityType: string]: Record<string, unknown>;
}

export const state = signal<NormalizedState>({});

/** True once the IndexedDB mirror has been read into memory at startup. */
export const hydrated = signal(false);

/** Load whatever is already mirrored in IndexedDB into memory. No network call. */
export async function hydrateFromMirror(): Promise<void> {
  if (hydrated.value) return;
  if (hydrateInflight) {
    await hydrateInflight;
    return;
  }
  const startedIn = storeEpoch;
  hydrateInflight = (async () => {
    const rows = await idbGetAll();
    if (startedIn !== storeEpoch) return; // signed out while reading: drop it
    const next: NormalizedState = {};
    let primed = 0;
    for (const [key, value] of Object.entries(rows)) {
      if (key.startsWith(RESOURCE_IDB_PREFIX)) {
        primeMirroredResource(key.slice(RESOURCE_IDB_PREFIX.length), value);
        primed += 1;
      } else {
        next[key] = value as Record<string, unknown>;
      }
    }
    // Saved copies exist: hold their revalidation until the version check
    // has said which of them still stand (or 8 s, whichever comes first).
    if (primed > 0) holdRevalidationForVersionCheck();
    state.value = next;
    hydrated.value = true;
  })().finally(() => {
    hydrateInflight = null;
  });
  await hydrateInflight;
}

/** Replace one entity table (e.g. after an initial snapshot fetch) and mirror it. */
export async function putEntityTable(entityType: EntityType, rows: Record<string, unknown>): Promise<void> {
  state.value = { ...state.value, [entityType]: rows };
  await idbPut(entityType, rows);
}

/** Apply a single entity upsert (e.g. from a delta) and mirror it. */
export async function upsertEntity(entityType: EntityType, id: EntityId, row: unknown): Promise<void> {
  const table = { ...(state.value[entityType] ?? {}) };
  table[String(id)] = row;
  state.value = { ...state.value, [entityType]: table };
  await idbPut(entityType, table);
}

export function getEntityTable(entityType: EntityType): Record<string, unknown> {
  return state.value[entityType] ?? {};
}

/** Wipe the in-memory store and its IndexedDB mirror. Used by sign-out. */
export async function clearStore(): Promise<void> {
  storeEpoch += 1;
  hydrateInflight = null;
  state.value = {};
  hydrated.value = false;
  clearResourceCache();
  await idbClearAll();
}

// ---------------------------------------------------------------------------
// Resource cache — F-44 fix.
//
// Page-level reads (the People roster, the Company profiles+documents, My
// Desk's files, a single person's profile drill-in) go through here instead
// of `useState` + `useEffect(..., [])` in the page component. Preact
// unmounts a page on route change, so component-local state was refetching
// every time a page came back into view — that is finding F-44, and it
// violates INV-1 ("navigation between pages = 0 network calls").
//
// The cache is a plain module-level Map, so it survives a page unmounting
// and remounting (a route change) exactly like the rest of this store does.
// A key is fetched at most once: the first caller to ask for a key that has
// never been requested triggers `fetcher()`; every later ask — including one
// from a freshly remounted page after navigating away and back — reads the
// cached result from memory. `clearStore()` (sign-out) wipes it, same as the
// entity table above, so the next person on a shared desktop never sees a
// previous person's cached page data.
export type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'fault'; error: string };

interface ResourceEntry<T> {
  load: Load<T>;
  listeners: Set<() => void>;
  inflight?: Promise<void>;
  /** True when this entry's only copy came from the IndexedDB mirror at
   * startup — `ready`, but never fetched in this session. Exactly these get one
   * revalidation; see the note on `useResource`. Cleared once the network
   * answers, so navigating away and back stays a zero-network operation. */
  mirrored?: boolean;
}

const resourceCache = new Map<string, ResourceEntry<unknown>>();

/** Bumped when the owner Refresh (or view-as) drops the cache, so mounted
 * `useResource` readers fetch again instead of sitting on a dead entry. */
export const resourceEpoch = signal(0);

function getResourceEntry<T>(key: string): ResourceEntry<T> {
  let entry = resourceCache.get(key) as ResourceEntry<T> | undefined;
  if (!entry) {
    entry = { load: { status: 'loading' }, listeners: new Set() };
    resourceCache.set(key, entry as ResourceEntry<unknown>);
  }
  return entry;
}

function setResourceLoad<T>(entry: ResourceEntry<T>, load: Load<T>): void {
  entry.load = load;
  entry.listeners.forEach((listener) => listener());
}

/**
 * Read (and, the first time, load) a cached resource by `key`.
 *
 * Pass `key: null` to skip entirely — used by a drill-in view (e.g. a
 * person's profile) before anything is selected, so it never fires a
 * fetch for a key that does not make sense yet.
 *
 * A fetch happens when this key has no entry yet (fresh key), or when the only
 * copy we hold came from the IndexedDB mirror at startup. A key already
 * resolved from the network this session — to `ready` or to `fault` —
 * is never fetched again by this hook; that is what makes navigating away and
 * back a zero-network operation.
 *
 * Owner, 2026-09-07: that mirror copy must be REVALIDATED once, or it is
 * permanent. Week 35 was closed in the database and managers kept seeing it in
 * the pay-period dropdown through reload after reload, because the hydrated
 * copy is primed as `ready` and this hook then refused to ask the server ever
 * again. Stale data that no refresh can clear is worse than a spinner: the
 * cached copy still renders immediately, and it is replaced the moment the
 * network answers.
 */
export function useResource<T>(key: string | null, fetcher: () => Promise<T>): Load<T> {
  const [, forceRender] = useState(0);
  const epoch = resourceEpoch.value;
  const entry = key !== null ? getResourceEntry<T>(key) : null;

  useEffect(() => {
    if (!entry) return undefined;
    const listener = (): void => forceRender((n) => n + 1);
    entry.listeners.add(listener);

    const needsRevalidate = entry.load.status === 'ready' && entry.mirrored === true;
    if ((entry.load.status === 'loading' || needsRevalidate) && !entry.inflight) {
      const run = (): Promise<void> => {
        entry.inflight = fetcher()
          .then((data) => {
            entry.inflight = undefined;
            entry.mirrored = false;
            setResourceLoad(entry, { status: 'ready', data });
            // Slow-moving data is kept across visits (lib/dataVersions.ts):
            // saved the first time it is fetched, not only after a refetch.
            if (isKeptAcrossVisits(key!) && !NEVER_PERSISTED_RESOURCE_KEYS.has(key!)) {
              void idbPut(`${RESOURCE_IDB_PREFIX}${key}`, data).catch(() => {});
            }
          })
          .catch((err: unknown) => {
            entry.inflight = undefined;
            // A revalidation that fails leaves the mirrored copy standing — the
            // manager keeps a working screen offline. Only a first load faults.
            if (needsRevalidate) { entry.mirrored = false; return; }
            setResourceLoad(entry, {
              status: 'fault',
              error: err instanceof Error ? err.message : String(err),
            });
          });
        return entry.inflight;
      };
      if (needsRevalidate && versionCheck && isKeptAcrossVisits(key!)) {
        // A kept copy waits for the version check (owner, 2026-09-26: the
        // page you land on used to re-ask for its data once per reload).
        // Confirmed: the check cleared `mirrored`, nothing to fetch. Dropped:
        // this entry is gone and its replacement fetched on the epoch bump.
        // Not settled (a failed check): revalidate as before.
        entry.inflight = versionCheck.then(() => {
          entry.inflight = undefined;
          if (!entry.mirrored || resourceCache.get(key!) !== entry) return undefined;
          return run();
        });
      } else {
        void run();
      }
    }

    return () => {
      entry.listeners.delete(listener);
    };
    // Re-run when the key changes, or when Refresh / view-as drops the cache
    // (resourceEpoch). Not on every render, and not merely because the page
    // remounted with the same key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, epoch]);

  return entry ? entry.load : { status: 'loading' };
}

/**
 * Re-fetch a resource that is already cached, in place.
 *
 * `useResource` deliberately never re-fetches a key that resolved — that is
 * what makes navigating away and back cost no network. But a page that WRITES
 * (Manager Entry submits a line, then voids one) has to show the result of its
 * own write, and re-mounting is not a refresh. This is the one escape hatch:
 * the caller names the key it just invalidated, and every mounted reader of
 * that key re-renders with the new data.
 *
 * The old data stays on screen while the new fetch is in flight — a spinner
 * that replaces a table the manager was reading is worse than a half-second
 * of slightly stale rows.
 */
export async function refetchResource<T>(key: string, fetcher: () => Promise<T>): Promise<void> {
  const entry = getResourceEntry<T>(key);
  const epoch = resourceEpoch.value;
  try {
    const data = await fetcher();
    if (resourceEpoch.value !== epoch || resourceCache.get(key) !== entry) return;
    await persistResource(key, data);
  } catch (err) {
    if (resourceEpoch.value !== epoch || resourceCache.get(key) !== entry) return;
    setResourceLoad(entry, {
      status: 'fault',
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Forget every cached resource, so the next reader fetches again.
 *
 * Real production use, not a test hook: changing the view-as lens (lib/viewAs.ts)
 * has to drop everything fetched under the previous lens. A cache that survives
 * a lens change would keep serving the owner's four clinics inside a
 * one-clinic view — and it would look like the lens worked.
 *
 * Note this does NOT re-render mounted readers on its own; the caller reloads
 * the data that follows (see `setViewAs`).
 */
/**
 * Put data a caller ALREADY has into the resource cache, so the first component
 * to `useResource` that key renders from it and fires no request.
 *
 * This exists for INV-1. The Tasks page's week/month picker needs the list of
 * periods; bootstrap already fetches exactly that list at sign-in, and without
 * this the page fetched it a second time — a network call on navigation, which
 * is the one thing INV-1 budgets at zero. Priming is not caching a guess: it is
 * the same response, from the same endpoint, handed over instead of refetched.
 */
export function peekResource<T>(key: string): T | null {
  const entry = resourceCache.get(key) as ResourceEntry<T> | undefined;
  return entry && entry.load.status === 'ready' ? entry.load.data : null;
}

/**
 * True when `key` has not yet settled from a FRESH network answer this
 * session — never requested, still loading, faulted, or only a mirrored
 * (IndexedDB) copy still waiting on its one revalidation (see `useResource`'s
 * own comment on `mirrored`).
 *
 * My Desk (N-090c, 2026-09-08): a panel that decides "do I have anything to
 * show" by peeking this cache (panelCatalog.tsx's `hasContent` functions)
 * must treat "don't know yet" as "assume yes, mount the real panel" — never
 * as "empty". Reading `peekResource() == null / .length === 0` for that
 * decision cannot tell "genuinely nothing due" apart from "nobody has asked
 * the server yet" or "this is yesterday's mirrored answer, still unrevalidated
 * this session" — and confusing the two hid the SPC panel behind "Nothing to
 * show right now" AFTER `service-compliance` had rows again, because the
 * panel that would have re-fetched (ComplianceAlerts) never got mounted to
 * do it: bootstrap itself skips a slice whose key already has ANY cached
 * answer, mirrored or not (`keepCompliance` in lib/bootstrap.ts), so a stale
 * mirrored copy left both the fetch AND the one component that could trigger
 * it permanently parked.
 */
export function resourcePendingFetch(key: string): boolean {
  const entry = resourceCache.get(key) as ResourceEntry<unknown> | undefined;
  if (!entry) return true;
  if (entry.load.status !== 'ready') return true;
  return entry.mirrored === true;
}

export function primeResource<T>(key: string, data: T): void {
  const entry = getResourceEntry<T>(key);
  setResourceLoad(entry, { status: 'ready', data });
}

/** Prime from the IndexedDB mirror at startup. Marked so `useResource` gives
 * it exactly one revalidation — a mirrored copy is last session's truth, and
 * without this it is permanent (a closed pay period that would not go away). */
export function primeMirroredResource<T>(key: string, data: T): void {
  const entry = getResourceEntry<T>(key);
  entry.mirrored = true;
  setResourceLoad(entry, { status: 'ready', data });
}

/**
 * Resource keys that must NEVER reach the IndexedDB mirror.
 *
 * My Desk's My Mail / My Calendar panels (client/src/lib/myGoogle.ts's
 * `MY_MAIL_KEY` / `MY_CALENDAR_KEY` — kept in sync with this set by hand,
 * standing rule 12: an explicit registry over inference) read the SIGNED-IN
 * person's own mailbox and calendar. A shared front-desk computer must never
 * carry a previous person's mail subjects or event titles on disk after they
 * sign out, so these two keys skip the mirror entirely — the in-memory cache
 * still updates (so a mounted panel/Refresh sees the fresh answer), only the
 * `idbPut` below is skipped. A CLOSED set: add a key here only for something
 * this sensitive, never as a general-purpose opt-out a feature reaches for
 * on its own.
 */
const NEVER_PERSISTED_RESOURCE_KEYS: ReadonlySet<string> = new Set([
  'my-mail', 'my-calendar',
  // Manager Entry (Cursor review #36): pay rates, roster changes and new-hire
  // details for the clinic. 'manager-entry' is lib/managerEntry.ts's
  // MANAGER_ENTRY_KEY, kept in sync by hand like the two above.
  'manager-entry',
]);

/** Prime the in-memory cache AND keep it across reloads, like entity tables —
 * except for `NEVER_PERSISTED_RESOURCE_KEYS`, which stay in memory only. */
export async function persistResource<T>(key: string, data: T): Promise<void> {
  primeResource(key, data);
  if (NEVER_PERSISTED_RESOURCE_KEYS.has(key)) return;
  await idbPut(`${RESOURCE_IDB_PREFIX}${key}`, data);
}

export function snapshotReadyResources(
  keep: (key: string) => boolean,
): Array<{ key: string; data: unknown }> {
  const out: Array<{ key: string; data: unknown }> = [];
  for (const [key, entry] of resourceCache) {
    if (keep(key) && entry.load.status === 'ready') {
      out.push({ key, data: entry.load.data });
    }
  }
  return out;
}

export function clearResourceCache(): void {
  resourceCache.clear();
  resourceEpoch.value += 1;
}

/** Test-only: forget every cached resource between test files/cases. */
export function __resetResourceCacheForTests(): void {
  clearResourceCache();
}

// --- Kept across visits (owner, 2026-09-24) --------------------------------
//
// lib/dataVersions.ts registers the keys of slow-moving data (People, Pay &
// Hours, Payroll, Company). Such a key is saved to the mirror the first time
// it loads, and at the next visit it is either CONFIRMED (its group's server
// fingerprint did not move — no refetch at all) or DROPPED (it moved — fetch
// again). A pattern ending in ':' or '-' is a prefix; anything else is exact.

const keptPatterns: string[] = [];

// --- The version-check gate (owner, 2026-09-26) ------------------------------
// The mirror is read at startup, and lib/dataVersions.ts asks the server one
// question per visit. A page that mounts in between used to revalidate its
// saved copy straight away — the page you land on cost one request per
// reload even when nothing had changed. While this gate is up, a KEPT key's
// revalidation waits for the check's answer (see useResource). It is raised
// when the mirror primes anything and lowered when the check settles — or
// after `maxMs` regardless, so a visit with no check (signed out, offline)
// still revalidates as before.
let versionCheck: Promise<void> | null = null;
let releaseVersionCheck: (() => void) | null = null;

export function holdRevalidationForVersionCheck(maxMs = 8000): void {
  if (versionCheck) return;
  const gate = new Promise<void>((resolve) => {
    releaseVersionCheck = resolve;
    setTimeout(resolve, maxMs);
  }).finally(() => {
    if (versionCheck === gate) { versionCheck = null; releaseVersionCheck = null; }
  });
  versionCheck = gate;
}

/** The version check has confirmed or dropped every kept key (or failed). */
export function versionCheckSettled(): void {
  releaseVersionCheck?.();
}

/** Test-only: is the gate up right now? */
export function __revalidationHeldForTests(): boolean {
  return versionCheck !== null;
}

export function keepAcrossVisits(patterns: string[]): void {
  for (const p of patterns) if (!keptPatterns.includes(p)) keptPatterns.push(p);
}

export function keyMatches(key: string, patterns: string[]): boolean {
  return patterns.some((p) => (p.endsWith(':') || p.endsWith('-') ? key.startsWith(p) : key === p));
}

function isKeptAcrossVisits(key: string): boolean {
  return keyMatches(key, keptPatterns);
}

/** The saved copies matching `patterns` are current — treat them as fetched
 * this visit, so no reader asks the server again. */
export function confirmMirrored(patterns: string[]): number {
  let n = 0;
  for (const [key, entry] of resourceCache) {
    if (entry.mirrored && keyMatches(key, patterns)) { entry.mirrored = false; n += 1; }
  }
  return n;
}

/** The saved copies matching `patterns` are out of date — forget them (memory
 * and mirror) so the next reader fetches fresh. Mounted readers re-render. */
export async function dropResources(patterns: string[]): Promise<number> {
  const keys = [...resourceCache.keys()].filter((k) => keyMatches(k, patterns));
  for (const k of keys) resourceCache.delete(k);
  await idbDeleteKeys(keys.map((k) => `${RESOURCE_IDB_PREFIX}${k}`)).catch(() => {});
  if (keys.length) resourceEpoch.value += 1;
  return keys.length;
}

/** Same, for whole entity tables (Payroll's pay periods, reviews, extras). */
export async function dropEntityTables(types: string[]): Promise<void> {
  if (!types.length) return;
  const next = { ...state.value } as Record<string, unknown>;
  for (const t of types) delete next[t];
  state.value = next as NormalizedState;
  await idbDeleteKeys(types).catch(() => {});
}
